import type { Permission } from '../hooks/useRoles';
import {
  FIELD_ABILITIES, STRUCTURAL_ABILITIES, GRANTABLE_STAGES,
} from './permissions-vocab';
import './StagePermissionMatrix.css';

interface Props {
  permissions: Permission[];
  /** The currently-scoped job type from the chip selector. "*" or single id. */
  jobTypeId: string;
  disabled: boolean;
  onToggle: (action: string, stage: string, wasChecked: boolean) => void;
  /**
   * The role's display name. Used to detect superuser roles (Admin) so we can
   * show an explanatory placeholder instead of an empty grid.
   */
  roleName?: string;
}

const ROW_FAMILIES = [
  { label: 'FIELD OPS',     abilities: FIELD_ABILITIES },
  { label: 'STRUCTURAL OPS', abilities: STRUCTURAL_ABILITIES },
] as const;

export function StagePermissionMatrix({ permissions, jobTypeId, disabled, onToggle, roleName }: Props) {
  // C-11 (walkthrough 2026-05-11) — Admin / superuser roles don't use
  // per-stage operator permissions (they have manage:* tenant-wide
  // authority that bypasses stage-level gating). Showing the empty grid
  // looks like a bug; render an explicit placeholder instead.
  const isSuperuser =
    roleName === 'Admin' ||
    permissions.some((p) => p.action === 'manage:*');

  if (isSuperuser) {
    return (
      <div className="spm-section">
        <div className="rb-section-title">Stage Permissions</div>
        <div className="stage-permissions__placeholder">
          <p className="stage-permissions__placeholder-title">
            Admin has implicit access to all stage operations.
          </p>
          <p className="stage-permissions__placeholder-body">
            Stage-level permissions don&apos;t apply to roles with tenant-wide authority.
            Configure per-stage operator permissions on Operator / Senior Operator /
            Supervisor roles instead.
          </p>
        </div>
      </div>
    );
  }

  const grantSet = new Set(
    permissions
      .filter((p) => p.jobTypeId === jobTypeId || p.jobTypeId === '*')
      .map((p) => `${p.action}|${p.stage}`),
  );

  function isExplicitlyChecked(action: string, stage: string): boolean {
    return grantSet.has(`${action}|${stage}`);
  }

  function isCoveredByWildcard(action: string, stage: string): boolean {
    if (stage === '*') return false;
    return grantSet.has(`${action}|*`);
  }

  return (
    <div className="spm-section">
      <div className="rb-section-title">Stage Permissions</div>
      <div className="stage-permissions-scroller">
        <div
          className="spm-grid"
          style={{ gridTemplateColumns: `var(--spm-label-col-width) repeat(${GRANTABLE_STAGES.length}, var(--spm-col-width))` }}
        >
          <div className="spm-corner" />
          {GRANTABLE_STAGES.map((s) => (
            <div key={s.id} className="spm-col-header-cell">
              {/* Headers are upright VERTICAL text (writing-mode, see the CSS)
               * so any label length stays readable in a narrow column with no
               * clipping — replacing the old 45°-rotated span that truncated
               * ("IMPO.", "BATC."). The `title` + `aria-label` still carry the
               * full stage name for hover affordance and assistive tech. */}
              <span className="spm-col-header" title={s.label} aria-label={s.label}>
                {s.label}
              </span>
            </div>
          ))}
          {ROW_FAMILIES.map((family) => (
            <FamilyRows
              key={family.label}
              label={family.label}
              abilities={family.abilities}
              isChecked={isExplicitlyChecked}
              isCovered={isCoveredByWildcard}
              disabled={disabled}
              onToggle={onToggle}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

interface FamilyProps {
  label: string;
  abilities: typeof FIELD_ABILITIES | typeof STRUCTURAL_ABILITIES;
  isChecked: (a: string, s: string) => boolean;
  isCovered: (a: string, s: string) => boolean;
  disabled: boolean;
  onToggle: Props['onToggle'];
}

function FamilyRows({ label, abilities, isChecked, isCovered, disabled, onToggle }: FamilyProps) {
  return (
    <>
      <div className="spm-family-label" style={{ gridColumn: `1 / span ${GRANTABLE_STAGES.length + 1}` }}>
        {label}
      </div>
      {abilities.map((ab) => (
        <Row key={ab.id} ability={ab} isChecked={isChecked} isCovered={isCovered}
             disabled={disabled} onToggle={onToggle} />
      ))}
    </>
  );
}

interface RowProps {
  ability: { id: string; label: string };
  isChecked: (a: string, s: string) => boolean;
  isCovered: (a: string, s: string) => boolean;
  disabled: boolean;
  onToggle: Props['onToggle'];
}

function Row({ ability, isChecked, isCovered, disabled, onToggle }: RowProps) {
  // "All-active" cue — when the wildcard `*` cell is checked, every other
  // cell in this row is implicitly granted. Tint the whole row so operators
  // can spot fully-granted abilities at a glance without reading each `·`.
  const rowAllActive = isChecked(ability.id, '*');
  return (
    <>
      <div className="spm-row-label">{ability.label}</div>
      {GRANTABLE_STAGES.map((s) => {
        const checked = isChecked(ability.id, s.id);
        const covered = !checked && isCovered(ability.id, s.id);
        const className = [
          'spm-cell',
          checked ? 'spm-cell--active' : '',
          covered ? 'spm-cell--covered' : '',
          disabled ? 'spm-cell--disabled' : '',
          rowAllActive ? 'spm-cell--row-active' : '',
        ].filter(Boolean).join(' ');

        return (
          <div
            key={s.id}
            className={className}
            role="checkbox"
            aria-checked={checked || covered}
            aria-label={`${ability.label} at ${s.label}`}
            tabIndex={disabled ? -1 : 0}
            onClick={() => !disabled && onToggle(ability.id, s.id, checked)}
            onKeyDown={(e) => {
              if (disabled) return;
              if (e.key === ' ' || e.key === 'Enter') {
                e.preventDefault();
                onToggle(ability.id, s.id, checked);
              }
            }}
          >
            {checked ? '✓' : covered ? '·' : ''}
          </div>
        );
      })}
    </>
  );
}
